// src/components/AyeOwnedSiteResult.ts
import "./AyeOwnedSiteResult.css";

export interface AyeOwnedSiteDefinition {
  aliases: string[];
  title: string;
  url: string;
  displayUrl: string;
  description: string;
  aikipediaTitle: string;
  aikipediaDescription: string;
  iconUrl?: string;
}

export interface AyeOwnedSiteMedia {
  faviconUrl?: string;
  imageUrl?: string;
}

const AYESEARCH_ICON_URL =
  "/static/ayesearch/Ayesearch_Icon.png";

const AIKIPEDIA_ICON_URL =
  "https://ayesearch.com/static/branding/aikipedia-d3b90016a842.png";

const ORECHAT_ICON_URL =
  "/static/ayesearch/OreChat_Icon.png";

const AMAILME_ICON_URL =
  "/static/ayesearch/Amailme_Icon.png";

const SHARED_SITELINKS = [
  {
    label: "Account",
    description: "Manage your profile and account settings.",
    url: "https://ayesearch.com/account",
  },
  {
    label: "AyeSearch Accounts",
    description: "Access your AyeSearch account services.",
    url: "https://ayesearch.com/accounts",
  },
  {
    label: "Cloud",
    description: "Open ADrive cloud storage and files.",
    url: "https://adrive.ayesearch.com",
  },
  {
    label: "Images",
    description: "Explore AyeSearch image results.",
    url: "https://ayesearch.com/search/images",
  },
  {
    label: "Apps on AyeSearch Store",
    description: "Discover apps on the AyeSearch Store.",
    url: "https://aappstore.ayesearch.com",
  },
  {
    label: "AyeSearch Games",
    description: "Browse games across AyeSearch.",
    url: "https://agamestore.ayesearch.com",
  },
] as const;

const OWNED_SITES: AyeOwnedSiteDefinition[] = [
  {
    aliases: ["ayesearch", "aye search", "ayesearch.com"],
    title: "AyeSearch",
    url: "https://ayesearch.com",
    displayUrl: "https://ayesearch.com",
    description:
      "Search the web with AyeSearch across webpages, images, apps, services, and the growing AyeSearch network.",
    aikipediaTitle: "AyeSearch - Aikipedia",
    aikipediaDescription:
      "AyeSearch is a search and web-services platform built around web discovery, search tools, connected accounts, cloud services, applications, communications, and other online experiences. It brings multiple AyeSearch products together through a unified search and account ecosystem.",
  },
  {
    aliases: ["adrive", "a drive", "adrive.ayesearch.com"],
    title: "ADrive",
    url: "https://adrive.ayesearch.com",
    displayUrl: "https://adrive.ayesearch.com",
    description:
      "ADrive is the AyeSearch cloud experience for accessing and organizing files, documents, and connected storage.",
    aikipediaTitle: "ADrive - Aikipedia",
    aikipediaDescription:
      "ADrive is an AyeSearch cloud service. This is a placeholder Aikipedia reference result for the service.",
  },
  {
    aliases: [
      "aikipedia",
      "ayekipedia",
      "aye kipedia",
      "aikipedia.ayesearch.com",
    ],
    title: "Aikipedia",
    url: "https://aikipedia.ayesearch.com",
    displayUrl: "https://aikipedia.ayesearch.com",
    description:
      "Aikipedia is AyeSearch's reference and knowledge experience for discovering people, places, topics, and structured information.",
    iconUrl: AIKIPEDIA_ICON_URL,
    aikipediaTitle: "Aikipedia",
    aikipediaDescription:
      "Explore articles about people, places, and topics on Aikipedia, AyeSearch’s knowledge and reference service.",
  },
  {
    aliases: ["orechat", "ore chat", "orechat.ayesearch.com"],
    title: "OreChat",
    iconUrl: ORECHAT_ICON_URL,
    url: "https://orechat.ayesearch.com",
    displayUrl: "https://orechat.ayesearch.com",
    description:
      "OreChat is AyeSearch's communication experience for messaging, communities, voice, and connected conversations.",
    aikipediaTitle: "OreChat - Aikipedia",
    aikipediaDescription:
      "OreChat is an AyeSearch communication service centered on messaging, communities, voice conversations, and connected social experiences. It is designed to support individual conversations, group spaces, and community interaction as part of the broader AyeSearch ecosystem.",
  },
  {
    aliases: ["anotes", "anote", "anotes.ayesearch.com"],
    title: "ANotes",
    url: "https://anotes.ayesearch.com",
    displayUrl: "https://anotes.ayesearch.com",
    description:
      "ANotes is an AyeSearch notes and writing experience for creating, organizing, and working with structured content.",
    aikipediaTitle: "ANotes - Aikipedia",
    aikipediaDescription:
      "ANotes is an AyeSearch notes and productivity service. This is a placeholder Aikipedia reference result.",
  },
  {
    aliases: ["updates", "ayesearch updates", "updates.ayesearch.com"],
    title: "AyeSearch Updates",
    url: "https://updates.ayesearch.com",
    displayUrl: "https://updates.ayesearch.com",
    description:
      "AyeSearch Updates provides release information, product updates, and software update experiences across AyeSearch products.",
    aikipediaTitle: "AyeSearch Updates - Aikipedia",
    aikipediaDescription:
      "AyeSearch Updates is the update and release service for AyeSearch products. This is a placeholder Aikipedia reference result.",
  },
  {
    aliases: [
      "ayesearch ads",
      "aye search ads",
      "ads",
      "ads.ayesearch.com",
    ],
    title: "AyeSearch Ads",
    url: "https://ads.ayesearch.com",
    displayUrl: "https://ads.ayesearch.com",
    description:
      "AyeSearch Ads helps businesses create and manage advertising experiences across the AyeSearch network.",
    aikipediaTitle: "AyeSearch Ads - Aikipedia",
    aikipediaDescription:
      "AyeSearch Ads is the advertising platform for the AyeSearch network. This is a placeholder Aikipedia reference result.",
  },
  {
    aliases: [
      "aappstore",
      "a app store",
      "ayesearch app store",
      "aappstore.ayesearch.com",
    ],
    title: "AAppStore",
    url: "https://aappstore.ayesearch.com",
    displayUrl: "https://aappstore.ayesearch.com",
    description:
      "AAppStore is AyeSearch's app discovery destination for browsing apps and connected software experiences.",
    aikipediaTitle: "AAppStore - Aikipedia",
    aikipediaDescription:
      "AAppStore is the AyeSearch application marketplace. This is a placeholder Aikipedia reference result.",
  },
  {
    aliases: [
      "agamestore",
      "a game store",
      "agamestore.ayesearch.com",
    ],
    title: "AGameStore",
    url: "https://agamestore.ayesearch.com",
    displayUrl: "https://agamestore.ayesearch.com",
    description:
      "AGameStore is AyeSearch's game discovery destination for games, releases, and connected gaming experiences.",
    aikipediaTitle: "AGameStore - Aikipedia",
    aikipediaDescription:
      "AGameStore is the AyeSearch games marketplace. This is a placeholder Aikipedia reference result.",
  },
  {
    aliases: [
      "amailme",
      "amail",
      "a mail",
      "amailme.ayesearch.com",
    ],
    title: "AMailMe",
    iconUrl: AMAILME_ICON_URL,
    url: "https://amailme.ayesearch.com",
    displayUrl: "https://amailme.ayesearch.com",
    description:
      "AMailMe is AyeSearch's email service for sending, receiving, and managing mail with an AyeSearch account.",
    aikipediaTitle: "AMailMe - Aikipedia",
    aikipediaDescription:
      "AMailMe is the AyeSearch email service for sending, receiving, and managing electronic mail through an AyeSearch account. It is designed to connect with other AyeSearch services while providing a dedicated mailbox and communication experience across supported devices.",
  },

  {
    aliases: [
      "ayelinks",
      "aye links",
      "ayelinks.com",
    ],
    title: "AyeLinks",
    url: "https://ayelinks.com",
    displayUrl: "https://ayelinks.com",
    description:
      "AyeLinks is a link-in-bio and profile-links experience for sharing important destinations from one page.",
    aikipediaTitle: "AyeLinks - Aikipedia",
    aikipediaDescription:
      "AyeLinks is an AyeSearch link-sharing service. This is a placeholder Aikipedia reference result.",
  },
];

function normalizeLookup(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "")
    .replace(/[^a-z0-9.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function findAyeOwnedSite(
  query: string,
): AyeOwnedSiteDefinition | null {
  const normalizedQuery = normalizeLookup(query);

  if (!normalizedQuery) {
    return null;
  }

  for (const site of OWNED_SITES) {
    const matches = site.aliases.some(
      (alias) =>
        normalizeLookup(alias) === normalizedQuery,
    );

    if (matches) {
      return site;
    }
  }

  return null;
}

export function isRelevantResultForAyeOwnedSite(
  site: AyeOwnedSiteDefinition,
  result: {
    url?: string;
    canonical_url?: string;
  },
): boolean {
  const rawUrl = result.canonical_url || result.url || "";

  try {
    const resultHost = new URL(rawUrl).hostname
      .toLowerCase()
      .replace(/^www\./, "");
    const siteHost = new URL(site.url).hostname
      .toLowerCase()
      .replace(/^www\./, "");

    return (
      resultHost === siteHost ||
      resultHost.endsWith(`.${siteHost}`)
    );
  } catch {
    return false;
  }
}

// Backward-compatible export for CompanyLayout.ts and older callers.
// Keep the canonical helper name above while preserving the accepted
// FeaturedSite import contract used elsewhere in the frontend.
export const isRelevantResultForFeaturedSite =
  isRelevantResultForAyeOwnedSite;

export function renderAyeOwnedSiteResult(
  site: AyeOwnedSiteDefinition,
  media: AyeOwnedSiteMedia = {},
): string {
  const title = escapeHtml(site.title);
  const iconUrl = escapeHtml(
    media.faviconUrl?.trim() ||
      site.iconUrl ||
      AYESEARCH_ICON_URL,
  );
  const url = escapeHtml(site.url);
  const displayUrl = escapeHtml(site.displayUrl);
  const description = escapeHtml(site.description);
  const aikipediaTitle =
    escapeHtml(site.aikipediaTitle);
  const aikipediaDescription =
    escapeHtml(site.aikipediaDescription);

  return `
    <section
      class="aye-owned-site-results"
      aria-label="${title} official AyeSearch result"
    >
      <article class="aye-owned-primary-result">
        <a
          class="aye-owned-source-row"
          href="${url}"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="aye-owned-favicon">
            <img
              src="${iconUrl}"
              alt=""
              loading="eager"
            />
          </span>

          <span class="aye-owned-source-copy">
            <strong>${title}</strong>
            <span>${displayUrl}</span>
          </span>
        </a>

        <h2 class="aye-owned-title">
          <a
            href="${url}"
            target="_blank"
            rel="noopener noreferrer"
          >
            ${title}
          </a>
        </h2>

        <p class="aye-owned-description">
          ${description}
        </p>

        <nav
          class="aye-owned-sitelinks"
          aria-label="${title} related links"
        >
          ${SHARED_SITELINKS.map(
            (link) => `
              <a
                class="aye-owned-sitelink"
                href="${escapeHtml(link.url)}"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span class="aye-owned-sitelink-text">
                  <strong>${escapeHtml(link.label)}</strong>
                  <span>${escapeHtml(link.description)}</span>
                </span>

                <span
                  class="aye-owned-sitelink-indicator"
                  aria-hidden="true"
                >
                  ↗
                </span>
              </a>
            `,
          ).join("")}
        </nav>
      </article>

      <article class="aye-owned-aikipedia-result">
        <a
          class="aye-owned-reference-source"
          href="https://aikipedia.ayesearch.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="aye-owned-favicon aye-owned-favicon-small">
            <img
              src="${AIKIPEDIA_ICON_URL}"
              alt=""
              loading="lazy"
            />
          </span>

          <span class="aye-owned-source-copy">
            <strong>Aikipedia</strong>
            <span>https://aikipedia.ayesearch.com</span>
          </span>
        </a>

        <h3 class="aye-owned-reference-title">
          <a
            href="https://aikipedia.ayesearch.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            ${aikipediaTitle}
          </a>
        </h3>

        <p class="aye-owned-reference-description">
          ${aikipediaDescription}
        </p>

        <span
          class="aye-owned-reference-corner-icon"
          aria-hidden="true"
        >
          <img
            src="${AIKIPEDIA_ICON_URL}"
            alt=""
            loading="lazy"
          />
        </span>
      </article>
    </section>
  `;
}
